import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Leaf, ArrowLeft, Download, ChevronRight, ChevronLeft, Check, X, ThumbsUp, ThumbsDown, Minus, Eye, MessageSquare, Wrench, AlertTriangle, BookOpen, Lightbulb, Printer, Scissors, Package, PenLine, Info, Camera, Trash2, ImageIcon, Loader2, Sparkles } from 'lucide-react';
import ChildSelector from '@/components/ChildSelector';
import AnswerSheetTipBanner from '@/components/AnswerSheetTipBanner';
import { isUnlocked } from '@/utils/entitlements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHASES = ['hook', 'iDo', 'weDo', 'youDo', 'plenary'];
const PHASE_LABELS = { hook: 'Hook', iDo: 'I Do', weDo: 'We Do', youDo: 'You Do', plenary: 'Wrap Up' };
const PHASE_COLORS = { hook: 'bg-kowhai text-fern-dark', iDo: 'bg-moana text-white', weDo: 'bg-fern text-white', youDo: 'bg-purple-500 text-white', plenary: 'bg-orange-500 text-white' };
// Earlier lesson sets describe `abstract.forms` with ad-hoc keys (strategy/
// look_for, term/definition, expression/working/result, ...) rather than the
// canonical name/value/explanation trio. Render those generically so no
// content is dropped: turn `look_for` into "Look for".
const humaniseKey = (k) =>
  String(k).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

// A handful of early lessons describe their visual as a shaped object
// (`type: story_mountain | mind_map | numberline | ...`) rather than a
// headers/values table. There is no bespoke component for those, so render the
// structure legibly instead of dropping the content on the floor.
const StructuredChart = ({ chart }) => {
  const entries = Object.entries(chart).filter(([k]) => k !== 'type');
  if (!entries.length) return null;
  const scalars = entries.filter(([, v]) => v === null || typeof v !== 'object');
  const groups = entries.filter(([, v]) => v && typeof v === 'object');
  return (
    <div className="mt-4 rounded-2xl border border-moana/20 bg-moana/5 p-4">
      {scalars.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 last:mb-0">
          {scalars.map(([k, v]) => (
            <span key={k} className="text-xs bg-white rounded-full px-3 py-1 border border-moana/20">
              <span className="font-semibold text-moana">{humaniseKey(k)}:</span>{' '}
              <span className="text-fern-dark">{String(v)}</span>
            </span>
          ))}
        </div>
      )}
      {groups.map(([k, v]) => (
        <div key={k} className="mb-3 last:mb-0">
          <p className="text-xs font-semibold text-moana uppercase tracking-wide mb-2">{humaniseKey(k)}</p>
          <div className="space-y-2">
            {(Array.isArray(v) ? v : [v]).map((item, i) => (
              <div key={i} className="bg-white rounded-xl px-3 py-2 border border-moana/10">
                {item && typeof item === 'object' ? (
                  Object.entries(item).map(([ik, iv]) => (
                    <p key={ik} className="text-sm text-slate-700">
                      <span className="font-semibold text-moana">{humaniseKey(ik)}: </span>
                      <span className="whitespace-pre-line">
                        {Array.isArray(iv) ? iv.join(' · ') : String(iv)}
                      </span>
                    </p>
                  ))
                ) : (
                  <p className="text-sm text-slate-700">{String(item)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const CPA_TABS = ['concrete', 'pictorial', 'abstract', 'variation'];
const CPA_LABELS = { concrete: 'Hands On', pictorial: 'Draw It', abstract: 'Write It', variation: 'What If?' };
const CPA_HINTS = {
  concrete: 'Use objects or counters to build the idea.',
  pictorial: 'Draw or map out the idea on paper.',
  abstract: 'Write the idea as a number sentence or rule.',
  variation: 'Try a twist — and spot the common mistake.',
};
const LEVELS = ['foundation', 'core', 'extension', 'challenge'];
const LEVEL_COLORS = { foundation: 'bg-green-500', core: 'bg-blue-500', extension: 'bg-purple-500', challenge: 'bg-orange-500' };
const LEVEL_HINTS = {
  foundation: 'Start here if you need a warm-up.',
  core: 'The main set — most kids start here.',
  extension: 'Stretch yourself — thinking hard now.',
  challenge: 'Top of the hill — try if you dare.',
};

export default function LessonViewer() {
  const { reference } = useParams();
  const navigate = useNavigate();
  const { authHeaders, childParam, activeChildName, user, bumpProgress } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [locked, setLocked] = useState(false); // 403 from /lessons/:reference — genuinely-owned-elsewhere-locked, not missing
  const [entitlementState, setEntitlementState] = useState({ loading: true, owned: false });
  const [phase, setPhase] = useState('hook');
  const [parentMode, setParentMode] = useState(false);
  const [parentModeConfirmOpen, setParentModeConfirmOpen] = useState(false);
  const PARENT_MODE_ACK_KEY = 'nestwise_parent_mode_ack_v1';

  const requestParentModeToggle = (nextChecked) => {
    // Switching OFF never needs a confirm.
    if (!nextChecked) {
      setParentMode(false);
      return;
    }
    // Switching ON: show the one-time warning unless the whānau has already acknowledged it.
    const acknowledged = typeof window !== 'undefined' && localStorage.getItem(PARENT_MODE_ACK_KEY) === '1';
    if (acknowledged) {
      setParentMode(true);
      return;
    }
    setParentModeConfirmOpen(true);
  };

  const confirmParentMode = () => {
    try { localStorage.setItem(PARENT_MODE_ACK_KEY, '1'); } catch (e) { /* localStorage disabled — ignore */ }
    setParentMode(true);
    setParentModeConfirmOpen(false);
  };
  const [cpaTab, setCpaTab] = useState('concrete');
  const [visitedCpa, setVisitedCpa] = useState(new Set(['concrete']));
  const [weDoStep, setWeDoStep] = useState(0);
  const [weDoRevealed, setWeDoRevealed] = useState(0);
  const [youDoLevel, setYouDoLevel] = useState('core');
  const [visitedLevels, setVisitedLevels] = useState(new Set(['core']));
  const [answers, setAnswers] = useState({});
  const [mcqAnswered, setMcqAnswered] = useState({});
  const [exitTicket, setExitTicket] = useState(null);
  const [evidence, setEvidence] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  const downloadFile = (url, filename) => {
    fetch(url, { headers: authHeaders })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(`Downloading ${filename}`);
      })
      .catch(() => toast.error('Download failed — please try again'));
  };

  useEffect(() => {
    setLocked(false);
    setLoading(true);
    axios.get(`${API}/lessons/${reference}${childParam}`, { headers: authHeaders }).then(r => { setLesson(r.data); setLoading(false); }).catch((e) => {
      if (e.response?.status === 403) setLocked(true);
      setLoading(false);
    });
    axios.get(`${API}/progress/${reference}${childParam}`, { headers: authHeaders }).then(r => {
      if (r.data) {
        setAnswers(r.data.answers || {});
        setExitTicket(r.data.exit_ticket);
        setEvidence(r.data.evidence_notes || '');
        setPhotos(r.data.evidence_photos || []);
      } else {
        // Different child = different progress; reset local state
        setAnswers({});
        setExitTicket(null);
        setEvidence('');
        setPhotos([]);
      }
    }).catch(() => {});
    axios.get(`${API}/resources/${reference}`).then(r => setResources(r.data || [])).catch(() => {});
  }, [reference, authHeaders, childParam]);

  // Entitlement check — runs once we know the lesson's subject and the active child.
  useEffect(() => {
    if (!lesson || !activeChildName) return;
    if (user?.unlimited_access) {
      setEntitlementState({ loading: false, owned: true });
      return;
    }
    let active = true;
    setEntitlementState(s => ({ ...s, loading: true }));
    axios.get(`${API}/billing/entitlements`, { headers: authHeaders }).then(r => {
      if (!active) return;
      const childEntitlements = (r.data.entitlements || []).filter(e => e.child_name === activeChildName);
      const owned = isUnlocked(childEntitlements, lesson, !!r.data.unlimited_access);
      setEntitlementState({ loading: false, owned });
    }).catch(() => active && setEntitlementState({ loading: false, owned: false }));
    return () => { active = false; };
  }, [lesson, activeChildName, authHeaders, user]);

  const saveProgress = async (completed = false) => {
    try {
      await axios.post(`${API}/progress`, {
        lesson_reference: reference, phase, answers,
        exit_ticket: exitTicket, evidence_notes: evidence, completed,
        child: activeChildName || undefined,
      }, { headers: authHeaders });
      // Invalidate progress caches across the app (Dashboard, Planner, Garden,
      // Reports) so returning to any of them reflects the new state immediately.
      bumpProgress();
      if (completed) {
        // Confetti moment — NZ palette (fern green, moana teal, kōwhai yellow)
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#2D5A46', '#1B93A4', '#F2C94C', '#3D7A5E', '#23B3C8'],
          ticks: 200,
        });
      }
      toast.success(completed ? 'Lesson marked complete!' : 'Progress saved');
      if (completed) {
        // Brief delay so the confetti + toast register, then return to Dashboard
        // to show the Native Garden growing.
        setTimeout(() => navigate('/'), 1400);
      }
    } catch { toast.error('Failed to save'); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/progress/${reference}/photo${childParam}`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      setPhotos(p => [...p, r.data]);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoDelete = async (photoId) => {
    try {
      await axios.delete(`${API}/progress/${reference}/photo/${photoId}${childParam}`, { headers: authHeaders });
      setPhotos(p => p.filter(ph => ph.id !== photoId));
      toast.success('Photo removed');
    } catch {
      toast.error('Failed to remove photo');
    }
  };

  const PhotoThumb = ({ photo }) => {
    const [src, setSrc] = useState(null);
    useEffect(() => {
      let active = true;
      let blobUrl = null;
      axios.get(`${API}/progress/${reference}/photo/${photo.id}`, { headers: authHeaders, responseType: 'blob' })
        .then(r => {
          if (!active) return;
          blobUrl = URL.createObjectURL(r.data);
          setSrc(blobUrl);
        })
        .catch(() => {});
      return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    }, [photo.id]);
    return (
      <div data-testid={`photo-thumb-${photo.id}`} className="relative group rounded-xl overflow-hidden bg-slate-100 aspect-square border border-slate-200">
        {src ? (
          <img src={src} alt={photo.filename} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
        )}
        <button data-testid={`photo-delete-${photo.id}`}
          onClick={() => handlePhotoDelete(photo.id)}
          className="absolute top-1 right-1 p-1.5 bg-white/90 hover:bg-red-500 hover:text-white text-slate-700 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all"
          aria-label="Delete photo">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5">
          <p className="text-white text-[10px] truncate">{photo.filename}</p>
        </div>
      </div>
    );
  };

  const handleMcq = (qId, selectedIdx, correctIdx) => {
    setMcqAnswered(p => ({ ...p, [qId]: selectedIdx }));
    setAnswers(p => ({ ...p, [qId]: { selected: selectedIdx, correct: selectedIdx === correctIdx } }));
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><div className="animate-pulse text-fern font-semibold">Loading lesson...</div></div>;
  if (!lesson && !locked) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><p className="text-slate-500">Lesson not found</p></div>;

  // ---- Paywall: gate the lesson if the active child does not own this subject/week.
  // `locked` covers a 403 from the lesson fetch itself (lesson stayed null); the
  // entitlementState check covers the case where the lesson loaded but the
  // (separate, per-lesson) entitlement check below says it's not owned. ----
  if (locked || (!entitlementState.loading && !entitlementState.owned)) {
    const subject = lesson?.subject || (reference.startsWith('maths') ? 'maths' : 'english');
    const subjLabel = subject === 'maths' ? 'Pāngarau / Maths' : 'Reo Pākehā / English';
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16">
            <Link to="/lessons">
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-fern rounded-lg gap-1">
                <ArrowLeft className="w-4 h-4" /> Back to Lessons
              </Button>
            </Link>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card data-testid="lesson-paywall" className="border-0 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.06)] rounded-3xl overflow-hidden">
            <CardContent className="p-8 sm:p-10 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-kowhai/30 mb-5">
                <Lightbulb className="w-7 h-7 text-kowhai-dark" />
              </div>
              <p className="text-xs uppercase tracking-[0.18em] text-fern font-semibold mb-3">Beyond the free trial</p>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-fern-dark tracking-tight mb-3">
                Unlock {subjLabel} for {activeChildName || 'this child'}
              </h1>
              <p className="text-slate-600 text-base leading-relaxed max-w-xl mx-auto mb-6">
                You’ve had a taste of NestWise with the first 2 free lessons. Pay once for lifetime access to
                every {subjLabel.split(' / ')[1]} lesson, printable, certificate and progress report — just
                for {activeChildName || 'your child'}.
              </p>
              <div className="inline-flex items-baseline gap-2 bg-white border border-fern/10 rounded-full px-5 py-2 mb-6 shadow-sm">
                <span className="text-slate-500 text-xs">NZD</span>
                <span className="font-heading text-2xl font-bold text-fern-dark">$99.99</span>
                <span className="text-slate-500 text-xs">one-off</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/pricing" data-testid="paywall-pricing-cta">
                  <Button className="bg-fern hover:bg-fern-light text-white rounded-full px-6 gap-2 w-full sm:w-auto">
                    Unlock now <ChevronRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/lessons" data-testid="paywall-back-cta">
                  <Button variant="outline" className="rounded-full px-6 border-fern/30 text-fern hover:bg-fern/5 w-full sm:w-auto">
                    Browse other lessons
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const phaseIdx = PHASES.indexOf(phase);

  const ParentCard = ({ type, children: content }) => {
    const styles = { say: 'say-card', do: 'do-card', expect: 'expect-card', watch: 'watch-card' };
    const icons = { say: <MessageSquare className="w-4 h-4" />, do: <Wrench className="w-4 h-4" />, expect: <Eye className="w-4 h-4" />, watch: <AlertTriangle className="w-4 h-4" /> };
    const labels = { say: 'Say This', do: 'Do This', expect: 'Expect from Your Child', watch: 'Watch For' };
    return (
      <div className={`${styles[type]} mb-4 animate-fadeInUp`}>
        <div className="flex items-center gap-2 mb-2 font-semibold text-sm">
          {icons[type]} {labels[type]}
        </div>
        <p className="text-sm leading-relaxed">{content}</p>
      </div>
    );
  };

  const renderHook = () => (
    <div className="space-y-6 animate-fadeInUp">
      <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl overflow-hidden">
        <div className="bg-gradient-to-r from-kowhai/20 to-kowhai/5 p-6 sm:p-8">
          <Badge className="bg-kowhai text-fern-dark border-0 rounded-full mb-4">The Hook</Badge>
          <p className="text-lg text-fern-dark leading-relaxed font-medium">{lesson.hook.scenario}</p>
        </div>
        <CardContent className="p-6 sm:p-8">
          <div className="bg-fern-50 rounded-2xl p-5 border-l-4 border-fern">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-5 h-5 text-fern mt-0.5 flex-shrink-0" />
              <p className="text-fern-dark font-medium leading-relaxed">{lesson.hook.question}</p>
            </div>
          </div>
          {parentMode && lesson.hook.parentScript && (
            <div className="mt-6 space-y-1">
              <h3 className="font-heading font-semibold text-fern-dark mb-3">Parent Script</h3>
              <ParentCard type="say">{lesson.hook.parentScript.sayThis}</ParentCard>
              <ParentCard type="do">{lesson.hook.parentScript.doThis}</ParentCard>
              <ParentCard type="expect">{lesson.hook.parentScript.expect}</ParentCard>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderIDo = () => {
    const section = lesson.iDo[cpaTab];
    const currentIdx = CPA_TABS.indexOf(cpaTab);
    const nextTab = currentIdx >= 0 && currentIdx < CPA_TABS.length - 1 ? CPA_TABS[currentIdx + 1] : null;
    const goToTab = (t) => {
      setCpaTab(t);
      setVisitedCpa(prev => new Set(prev).add(t));
    };
    return (
      <div className="space-y-6 animate-fadeInUp">
        {/* Guidance banner */}
        <div className="bg-moana-50 border border-moana/20 rounded-2xl px-4 py-3 flex items-start gap-3">
          <Info className="w-5 h-5 text-moana flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-moana-dark">
              <strong>Work through all 4 steps below before moving on.</strong>{' '}
              Each step shows the same idea in a different way — hands on, then drawings, then the written form, then a twist. Tap each step in order.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-xs font-semibold text-moana-dark bg-white rounded-full px-3 py-1 border border-moana/20">
            Step {currentIdx + 1} of {CPA_TABS.length}
          </div>
        </div>

        {/* CPA stepper — numbered, checkmarked, obvious */}
        <div data-testid="cpa-stepper" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CPA_TABS.map((tab, idx) => {
            const active = cpaTab === tab;
            const visited = visitedCpa.has(tab);
            return (
              <button
                key={tab}
                data-testid={`cpa-${tab}`}
                onClick={() => goToTab(tab)}
                className={`relative text-left rounded-2xl border-2 transition-all duration-200 px-3 py-3 flex items-start gap-3 ${
                  active
                    ? 'bg-moana text-white border-moana shadow-lg scale-[1.02]'
                    : visited
                      ? 'bg-white text-moana-dark border-moana/40 hover:border-moana'
                      : 'bg-white text-slate-500 border-slate-200 hover:border-moana/40'
                }`}
              >
                <span
                  className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${
                    active ? 'bg-white/25 text-white' : visited ? 'bg-moana text-white' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {visited && !active ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-sm leading-tight ${active ? 'text-white' : ''}`}>{CPA_LABELS[tab]}</div>
                  <div className={`text-[11px] leading-tight mt-0.5 ${active ? 'text-white/80' : 'text-slate-500'}`}>
                    {CPA_HINTS[tab]}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {section && (
          <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex items-center justify-center w-7 h-7 bg-moana text-white rounded-full text-xs font-bold">
                  {currentIdx + 1}
                </span>
                <h3 className="font-heading text-xl font-semibold text-moana">{section.title}</h3>
              </div>
              {section.instructions && <p className="text-slate-600 mb-6 leading-relaxed">{section.instructions}</p>}

              {/* Steps (concrete) */}
              {section.steps && (
                <div className="space-y-4">
                  {section.steps.map((s, i) => (
                    <div key={i} className="flex gap-4 animate-fadeInUp" style={{ animationDelay: `${i * 100}ms` }}>
                      <div className="w-8 h-8 rounded-full bg-moana text-white flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">{s.step}</div>
                      <div className="flex-1">
                        <p className="font-medium text-fern-dark whitespace-pre-line">{s.text}</p>
                        <p className="text-sm text-slate-500 mt-1 whitespace-pre-line">{s.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Chart (pictorial) — `values` is either a single row of cells
                  or, in earlier lesson sets, an array of such rows. */}
              {section.chart && section.chart.headers && (
                <div className="overflow-x-auto mt-4">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>{section.chart.headers.map((h, i) => <th key={i} className="bg-moana/10 text-moana text-xs font-semibold px-3 py-2 text-center border border-moana/20">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(section.chart.values?.[0])
                        ? section.chart.values
                        : [section.chart.values || []]
                      ).map((row, ri) => (
                        <tr key={ri}>
                          {(row || []).map((v, i) => (
                            <td key={i} className="text-center text-xl font-bold text-fern-dark px-3 py-4 border border-moana/20">
                              {v === null || v === undefined || v === '' ? '-' : String(v)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {section.chart && !section.chart.headers && <StructuredChart chart={section.chart} />}
              {section.explanation && <p className="text-slate-600 mt-4 leading-relaxed whitespace-pre-line">{section.explanation}</p>}

              {/* Forms (abstract) — canonical shape is name/value/explanation;
                  earlier lessons use ad-hoc keys, rendered generically. */}
              {section.forms && (
                <div className="space-y-4 mt-2">
                  {section.forms.map((f, i) => (
                    <div key={i} className="bg-slate-50 rounded-2xl p-5">
                      {f.name !== undefined && f.value !== undefined ? (
                        <>
                          <h4 className="font-semibold text-moana text-sm mb-1">{f.name}</h4>
                          <p className="text-xl font-heading font-bold text-fern-dark mb-2 whitespace-pre-line">{f.value}</p>
                          <p className="text-sm text-slate-500">{f.explanation}</p>
                        </>
                      ) : (
                        Object.entries(f).map(([k, v]) => (
                          <p key={k} className="text-sm text-slate-700 mb-1 last:mb-0">
                            <span className="font-semibold text-moana">{humaniseKey(k)}: </span>
                            <span className="whitespace-pre-line">{String(v)}</span>
                          </p>
                        ))
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Variation examples — canonical shape is original/changed;
                  earlier lessons use context/example. */}
              {section.examples && (
                <div className="space-y-4 mt-2">
                  {section.examples.map((ex, i) => (
                    <div key={i} className="bg-kowhai-50 rounded-2xl p-5">
                      {ex.original !== undefined || ex.changed !== undefined ? (
                        <div className="flex flex-wrap gap-3 items-center mb-2">
                          <Badge className="bg-slate-200 text-slate-700 border-0 rounded-full">{ex.original}</Badge>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                          <Badge className="bg-kowhai text-fern-dark border-0 rounded-full">{ex.changed}</Badge>
                        </div>
                      ) : (
                        <div className="mb-2">
                          {ex.context && <Badge className="bg-kowhai text-fern-dark border-0 rounded-full mb-2">{ex.context}</Badge>}
                          {ex.example && <p className="text-sm text-slate-700 whitespace-pre-line">{ex.example}</p>}
                        </div>
                      )}
                      {ex.explanation && <p className="text-sm text-slate-600">{ex.explanation}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Common Error */}
              {cpaTab === 'variation' && lesson.iDo.commonError && (
                <div className="mt-6 bg-red-50 rounded-2xl p-5 border-l-4 border-red-400">
                  <h4 className="font-semibold text-red-700 text-sm mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Common Error: {lesson.iDo.commonError.errorName}</h4>
                  <p className="text-sm text-red-600 mb-2"><strong>Example:</strong> {lesson.iDo.commonError.example}</p>
                  <p className="text-sm text-slate-600 mb-2">{lesson.iDo.commonError.explanation}</p>
                  <p className="text-sm text-fern font-medium">{lesson.iDo.commonError.correction}</p>
                </div>
              )}

              {/* Parent scripts */}
              {parentMode && section.parentScript && (
                <div className="mt-6 space-y-1">
                  <h3 className="font-heading font-semibold text-fern-dark mb-3">Parent Script</h3>
                  <ParentCard type="say">{section.parentScript.sayThis}</ParentCard>
                  <ParentCard type="do">{section.parentScript.doThis}</ParentCard>
                  <ParentCard type="expect">{section.parentScript.expect}</ParentCard>
                </div>
              )}
              {parentMode && cpaTab === 'variation' && lesson.iDo.commonError?.parentScript && (
                <div className="mt-4">
                  <ParentCard type="watch">{lesson.iDo.commonError.parentScript.watchFor || lesson.iDo.commonError.parentScript.sayThis}</ParentCard>
                </div>
              )}

              {/* Next-step affordance — makes the "you must click through all 4" mechanic obvious */}
              <div className="mt-6 pt-5 border-t border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="text-xs text-slate-500">
                  {visitedCpa.size < CPA_TABS.length
                    ? `${visitedCpa.size} of ${CPA_TABS.length} steps visited`
                    : 'All 4 steps visited — ready for "We Do".'}
                </div>
                {nextTab ? (
                  <Button
                    data-testid="cpa-next"
                    onClick={() => goToTab(nextTab)}
                    className="bg-moana hover:bg-moana-dark text-white rounded-full gap-2 px-5"
                  >
                    Next: {CPA_LABELS[nextTab]}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    data-testid="cpa-continue-wedo"
                    onClick={() => setPhase('weDo')}
                    className="bg-fern hover:bg-fern-dark text-white rounded-full gap-2 px-5"
                  >
                    Continue to "We Do"
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* MCQ section */}
        {lesson.mcQuestions && lesson.mcQuestions.length > 0 && (
          <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
            <CardContent className="p-6 sm:p-8">
              <h3 className="font-heading text-lg font-semibold text-moana mb-4">Quick Check</h3>
              {lesson.mcQuestions.map((q, qi) => (
                <div key={q.id} className="mb-6 last:mb-0">
                  <p className="font-medium text-fern-dark mb-3">{qi + 1}. {q.question}</p>
                  <div className="grid gap-2">
                    {q.options.map((opt, oi) => {
                      const answered = mcqAnswered[q.id] !== undefined;
                      const selected = mcqAnswered[q.id] === oi;
                      const isCorrect = oi === q.correctIndex;
                      let btnClass = 'bg-white border-2 border-slate-200 text-slate-700 hover:border-moana/50';
                      if (answered && selected && isCorrect) btnClass = 'bg-green-50 border-2 border-green-400 text-green-700 correct-answer';
                      else if (answered && selected && !isCorrect) btnClass = 'bg-red-50 border-2 border-red-400 text-red-700';
                      else if (answered && isCorrect) btnClass = 'bg-green-50 border-2 border-green-300 text-green-700';
                      return (
                        <button key={oi} data-testid={`mcq-${q.id}-opt-${oi}`}
                          onClick={() => !answered && handleMcq(q.id, oi, q.correctIndex)}
                          disabled={answered}
                          className={`text-left px-4 py-3 rounded-xl transition-all duration-200 text-sm flex items-center gap-3 ${btnClass}`}>
                          <span className="w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {answered && selected && isCorrect ? <Check className="w-3 h-3" /> : answered && selected && !isCorrect ? <X className="w-3 h-3" /> : String.fromCharCode(65 + oi)}
                          </span>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {mcqAnswered[q.id] !== undefined && (
                    <p className={`text-sm mt-2 p-3 rounded-xl ${mcqAnswered[q.id] === q.correctIndex ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {q.explanation}
                    </p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderWeDo = () => {
    const steps = lesson.weDo.steps;
    return (
      <div className="space-y-6 animate-fadeInUp">
        {/* Materials prompt */}
        <div className="bg-kowhai-50 rounded-2xl p-4 flex items-center gap-3 border border-kowhai/20">
          <PenLine className="w-5 h-5 text-kowhai-dark flex-shrink-0" />
          <p className="text-sm text-kowhai-dark"><strong>Before you start:</strong> Grab a pencil and paper (or whiteboard) — you will need them for this section! If your lesson has hands-on materials, get those ready too.</p>
        </div>

        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
          <CardContent className="p-6 sm:p-8">
            <p className="text-slate-600 mb-6 leading-relaxed">{lesson.weDo.introduction}</p>
            <div className="space-y-6">
              {steps.map((step, i) => {
                const stepVisible = i <= Math.min(weDoRevealed, steps.length - 1);
                const answerVisible = i < weDoRevealed || parentMode;
                return (
                <div key={i} className={`transition-all duration-500 ${stepVisible ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}`}>
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-fern text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-fern-dark text-base mb-2">{step.instruction}</p>
                      {stepVisible && (
                        <div className="animate-fadeInUp">
                          <div className="bg-kowhai-50 rounded-xl p-3 mb-3">
                            <p className="text-sm text-kowhai-dark"><strong>Hint:</strong> {step.hint}</p>
                          </div>
                          {answerVisible && (
                            <div className="bg-fern-50 rounded-xl p-3">
                              <p className="text-sm text-fern"><strong>Answer:</strong> {step.answer}</p>
                            </div>
                          )}
                          {parentMode && step.parentScript && (
                            <div className="mt-4 space-y-1">
                              <ParentCard type="say">{step.parentScript.sayThis}</ParentCard>
                              <ParentCard type="do">{step.parentScript.doThis}</ParentCard>
                              <ParentCard type="expect">{step.parentScript.expect}</ParentCard>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {i < steps.length - 1 && stepVisible && <hr className="border-slate-100 mt-6" />}
                </div>
                );
              })}
            </div>
            {weDoRevealed < steps.length && (
              <Button data-testid="reveal-next-step" onClick={() => setWeDoRevealed(p => p + 1)}
                className="mt-6 bg-fern hover:bg-fern-light text-white rounded-full gap-2">
                {weDoRevealed === steps.length - 1 ? 'Show Final Answer' : 'Show Next Step'} <ChevronRight className="w-4 h-4" />
              </Button>
            )}
            {weDoRevealed >= steps.length && (
              <p className="mt-6 text-sm text-green-600 font-medium flex items-center gap-2"><Check className="w-4 h-4" /> All steps revealed — ka pai!</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderYouDo = () => {
    const levelData = lesson.youDo[youDoLevel];
    if (!levelData) return null;
    const currentLevelIdx = LEVELS.indexOf(youDoLevel);
    const nextLevel = currentLevelIdx >= 0 && currentLevelIdx < LEVELS.length - 1 ? LEVELS[currentLevelIdx + 1] : null;
    const goToLevel = (l) => {
      setYouDoLevel(l);
      setVisitedLevels(prev => new Set(prev).add(l));
    };
    return (
      <div className="space-y-6 animate-fadeInUp">
        {/* Guidance banner */}
        <div className="bg-purple-50 border border-purple-200 rounded-2xl px-4 py-3 flex items-start gap-3">
          <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-purple-900">
              <strong>4 difficulty levels below — try them in order.</strong>{' '}
              Start at Foundation (warm-up), move through Core (main set), then stretch with Extension and Challenge. Multiple-choice is auto-marked; written questions are checked by a parent using the Answer Key in <strong>Parent Mode</strong>.
            </p>
            <p className="text-xs text-purple-800/80 mt-1.5 flex items-center gap-1.5 flex-wrap">
              <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                To switch to Parent Mode, use the <strong>Student · Parent toggle</strong> at the top-right of this page — the Answer Key will appear beside each question.
              </span>
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-xs font-semibold text-purple-900 bg-white rounded-full px-3 py-1 border border-purple-200">
            Level {currentLevelIdx + 1} of {LEVELS.length}
          </div>
        </div>

        {/* Level stepper — numbered, coloured, with checkmarks */}
        <div data-testid="level-stepper" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {LEVELS.map((lvl, idx) => {
            const data = lesson.youDo[lvl];
            if (!data) return null;
            const active = youDoLevel === lvl;
            const visited = visitedLevels.has(lvl);
            const colorMap = { foundation: 'green', core: 'blue', extension: 'purple', challenge: 'orange' };
            const c = colorMap[lvl];
            return (
              <button
                key={lvl}
                data-testid={`level-${lvl}`}
                onClick={() => goToLevel(lvl)}
                className={`relative text-left rounded-2xl border-2 transition-all duration-200 px-3 py-3 flex items-start gap-3 ${
                  active
                    ? `${LEVEL_COLORS[lvl]} text-white border-transparent shadow-lg scale-[1.02]`
                    : visited
                      ? `bg-white text-slate-800 border-${c}-300 hover:border-${c}-500`
                      : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                }`}
              >
                <span
                  className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${
                    active ? 'bg-white/25 text-white' : visited ? `${LEVEL_COLORS[lvl]} text-white` : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {visited && !active ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-sm leading-tight ${active ? 'text-white' : ''}`}>{data.label}</div>
                  <div className={`text-[11px] leading-tight mt-0.5 ${active ? 'text-white/85' : 'text-slate-500'}`}>
                    {LEVEL_HINTS[lvl]}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
          <CardContent className="p-6 sm:p-8">
            <p className="text-sm text-slate-500 mb-6 italic">{levelData.description}</p>
            <div className="space-y-6">
              {levelData.problems.map((prob, i) => (
                <div key={prob.id} className="animate-fadeInUp" style={{ animationDelay: `${i * 80}ms` }}>
                  <p className="font-medium text-fern-dark mb-3 whitespace-pre-line">{i + 1}. {prob.question}</p>
                  {prob.options ? (
                    <div className="grid gap-2 mb-3">
                      {prob.options.map((opt, oi) => {
                        const answered = answers[prob.id]?.selected !== undefined;
                        const selected = answers[prob.id]?.selected === oi;
                        const isCorrect = oi === prob.correctIndex;
                        let cls = 'bg-white border-2 border-slate-200 text-slate-700 hover:border-moana/50';
                        if (answered && selected && isCorrect) cls = 'bg-green-50 border-2 border-green-400 text-green-700';
                        else if (answered && selected && !isCorrect) cls = 'bg-red-50 border-2 border-red-400 text-red-700';
                        else if (answered && isCorrect) cls = 'bg-green-50 border-2 border-green-300 text-green-700';
                        return (
                          <button key={oi} data-testid={`prob-${prob.id}-opt-${oi}`}
                            onClick={() => { if (!answered) { setAnswers(p => ({ ...p, [prob.id]: { selected: oi, correct: oi === prob.correctIndex } })); } }}
                            disabled={answered}
                            className={`text-left px-4 py-3 rounded-xl transition-all text-sm ${cls}`}>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <Textarea data-testid={`answer-${prob.id}`}
                      placeholder="Write your answer here..."
                      className="rounded-xl bg-slate-50 focus:bg-white min-h-[80px] mb-3"
                      value={answers[prob.id]?.text || ''}
                      onChange={e => setAnswers(p => ({ ...p, [prob.id]: { ...p[prob.id], text: e.target.value } }))} />
                  )}
                  {parentMode && (
                    <div className="bg-fern-50 rounded-xl p-3 text-sm text-fern">
                      <strong>Answer Key:</strong> <span className="whitespace-pre-line">{prob.answer}</span>
                    </div>
                  )}
                  {i < levelData.problems.length - 1 && <hr className="border-slate-100 mt-4" />}
                </div>
              ))}
            </div>

            {/* Next-level affordance */}
            <div className="mt-8 pt-5 border-t border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="text-xs text-slate-500">
                {visitedLevels.size < LEVELS.length
                  ? `${visitedLevels.size} of ${LEVELS.length} levels visited`
                  : 'All 4 levels visited — nice work, ready to wrap up.'}
              </div>
              {nextLevel ? (
                <Button
                  data-testid="level-next"
                  onClick={() => goToLevel(nextLevel)}
                  className={`${LEVEL_COLORS[nextLevel]} hover:opacity-90 text-white rounded-full gap-2 px-5`}
                >
                  Next: {lesson.youDo[nextLevel]?.label}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  data-testid="level-continue-plenary"
                  onClick={() => setPhase('plenary')}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full gap-2 px-5"
                >
                  Continue to "Wrap Up"
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderPlenary = () => (
    <div className="space-y-6 animate-fadeInUp">
      {/* Intro explanation */}
      <div className="bg-fern-50 rounded-2xl p-4 flex items-start gap-3 border border-fern/20">
        <BookOpen className="w-5 h-5 text-fern flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-fern-dark"><strong>Wrap Up</strong> — This is the end of the lesson! Look at a worked example together, spot a common mistake, then tell us how confident you feel.</p>
        </div>
      </div>

      {/* Worked solution */}
      <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
        <CardContent className="p-6 sm:p-8">
          <Badge className="bg-orange-100 text-orange-700 border-0 rounded-full mb-4">Worked Example</Badge>
          <h3 className="font-heading text-lg font-semibold text-fern-dark mb-4 whitespace-pre-line">{lesson.plenary.workedSolution.question}</h3>
          <div className="space-y-3">
            {lesson.plenary.workedSolution.steps.map((s, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{s}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-kowhai-50 rounded-xl p-4">
            <p className="text-sm text-fern-dark"><strong>Key Insight:</strong> {lesson.plenary.workedSolution.keyInsight}</p>
          </div>
        </CardContent>
      </Card>

      {/* Common mistake */}
      <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
        <CardContent className="p-6 sm:p-8">
          <Badge className="bg-red-100 text-red-700 border-0 rounded-full mb-4">Watch Out For This Mistake</Badge>
          <div className="bg-red-50 rounded-2xl p-5 mb-4">
            <p className="text-sm text-red-700"><strong>Mistake:</strong> {lesson.plenary.commonMistake.mistake}</p>
          </div>
          <p className="text-sm text-slate-600 mb-2"><strong>Why it happens:</strong> {lesson.plenary.commonMistake.why}</p>
          <p className="text-sm text-fern font-medium"><strong>Fix:</strong> {lesson.plenary.commonMistake.fix}</p>
        </CardContent>
      </Card>

      {/* Exit ticket */}
      <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
        <CardContent className="p-6 sm:p-8 text-center">
          <Badge className="bg-fern-50 text-fern border-0 rounded-full mb-4">Exit Ticket / Tīkiti Putanga</Badge>
          <h3 className="font-heading text-xl font-semibold text-fern-dark mb-2">{lesson.plenary.exitTicket.statement}</h3>
          <p className="text-sm text-slate-500 italic mb-6">{lesson.plenary.exitTicket.teReo}</p>
          <p className="text-sm text-slate-600 mb-4">How confident are you?</p>
          <div className="flex justify-center gap-4">
            {[
              { val: 'up', icon: <ThumbsUp className="w-8 h-8" />, label: 'Got it!', color: 'text-green-500 hover:bg-green-50' },
              { val: 'middle', icon: <Minus className="w-8 h-8" />, label: 'Almost', color: 'text-kowhai hover:bg-kowhai-50' },
              { val: 'down', icon: <ThumbsDown className="w-8 h-8" />, label: 'Need help', color: 'text-red-400 hover:bg-red-50' },
            ].map(t => (
              <button key={t.val} data-testid={`exit-${t.val}`}
                onClick={() => setExitTicket(t.val)}
                className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all duration-200 ${exitTicket === t.val ? `${t.color} bg-opacity-10 ring-2 ring-current scale-110` : `${t.color}`}`}>
                {t.icon}
                <span className="text-xs font-medium">{t.label}</span>
              </button>
            ))}
          </div>
          {exitTicket && (
            <div className="mt-6">
              <Button data-testid="complete-lesson" onClick={() => saveProgress(true)}
                className="bg-fern hover:bg-fern-light text-white rounded-full px-8 gap-2 transition-all hover:scale-105">
                <Check className="w-4 h-4" /> Mark Lesson Complete
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Evidence box (parent mode) */}
      {parentMode && (
        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
          <CardContent className="p-6 sm:p-8">
            <h3 className="font-heading text-lg font-semibold text-fern-dark mb-4">Evidence Notes (for MoE records)</h3>
            <Textarea data-testid="evidence-notes" placeholder="Record observations, evidence of learning, and any notes for your records..."
              className="rounded-xl bg-slate-50 focus:bg-white min-h-[120px] mb-4" value={evidence} onChange={e => setEvidence(e.target.value)} />

            {/* Photo upload section */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-fern-dark text-sm flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" /> Evidence Photos
                  {photos.length > 0 && <span className="text-xs text-slate-500 font-normal">({photos.length})</span>}
                </h4>
                <label htmlFor="photo-upload" data-testid="photo-upload-label"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all ${uploading ? 'bg-slate-100 text-slate-400 cursor-wait' : 'bg-fern-50 text-fern hover:bg-fern hover:text-white'}`}>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  {uploading ? 'Uploading…' : 'Add Photo'}
                </label>
                <input id="photo-upload" data-testid="photo-upload-input" type="file" accept="image/*" capture="environment"
                  onChange={handlePhotoUpload} disabled={uploading} className="hidden" />
              </div>
              {photos.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No photos yet. Photos of completed worksheets, projects, or work samples will appear here and in the MoE Evidence Report.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {photos.map(ph => <PhotoThumb key={ph.id} photo={ph} />)}
                </div>
              )}
            </div>

            <Button data-testid="save-evidence" onClick={() => saveProgress(false)} className="bg-moana hover:bg-moana-light text-white rounded-full gap-2">
              Save Evidence Notes
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderPhase = () => {
    switch (phase) {
      case 'hook': return renderHook();
      case 'iDo': return renderIDo();
      case 'weDo': return renderWeDo();
      case 'youDo': return renderYouDo();
      case 'plenary': return renderPlenary();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link to="/lessons" data-testid="back-to-lessons">
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-fern rounded-lg gap-1">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
            </Link>
            <div className="hidden sm:block">
              <Badge className={`${lesson.subject === 'maths' ? 'bg-moana-50 text-moana' : 'bg-fern-50 text-fern'} border-0 rounded-full text-xs`}>
                {lesson.subject === 'maths' ? 'Maths' : 'English'} · T{lesson.term || 2} W{lesson.week}D{lesson.day}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ChildSelector />
            <div className="flex items-center gap-2 bg-sand-dark rounded-full px-3 py-1.5">
              <span className={`text-xs font-medium ${!parentMode ? 'text-fern' : 'text-slate-400'}`}>Student</span>
              <Switch data-testid="mode-toggle" checked={parentMode} onCheckedChange={requestParentModeToggle} className="data-[state=checked]:bg-fern" />
              <span className={`text-xs font-medium ${parentMode ? 'text-fern' : 'text-slate-400'}`}>Parent</span>
            </div>
            <Button data-testid="download-pdf" variant="ghost" size="sm" className="text-slate-400 hover:text-moana rounded-lg"
              onClick={() => downloadFile(`${API}/lessons/${reference}/pdf${childParam}`, `${reference}-worksheet.pdf`)}>
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Lesson title */}
        <div className="mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-fern-dark tracking-tight">{lesson.title}</h1>
          <p className="text-sm text-slate-500 italic mt-1">{lesson.teReoTitle}</p>
          <div className="bg-fern-50 rounded-2xl p-4 mt-3">
            <p className="text-xs text-fern uppercase tracking-wider font-semibold mb-1">Learning Objective</p>
            <p className="text-base sm:text-lg text-fern-dark font-medium leading-relaxed">{lesson.learningObjective}</p>
          </div>
        </div>

        {/* Materials & Printables */}
        {resources.length > 0 && (
          <Card data-testid="materials-panel" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl mb-6 no-print">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-5 h-5 text-kowhai-dark" />
                <h3 className="font-heading text-sm font-semibold text-fern-dark">Materials & Printables</h3>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {lesson.materials.map((m, i) => (
                  <span key={i} className="text-xs bg-kowhai-50 text-kowhai-dark px-3 py-1.5 rounded-full">{m}</span>
                ))}
              </div>

              {/* Gentle, one-time "Did you know?" nudge about answer sheets */}
              <AnswerSheetTipBanner hasAnswerSheet={resources.some(r => r.has_answer_sheet)} />

              <div className="flex flex-col gap-3">
                {resources.map(r => (
                  <div key={r.id} className={`grid gap-2 ${r.has_answer_sheet ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                    {/* Primary: blank printable for the student */}
                    <button
                      data-testid={`resource-${r.id}`}
                      onClick={() => downloadFile(`${API}/resources/${reference}/${r.id}/pdf${childParam}`, `nestwise-${r.id}.pdf`)}
                      className="flex items-start gap-3 bg-white border border-slate-200 rounded-2xl p-4 text-left hover:border-moana/40 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-moana/10 flex items-center justify-center flex-shrink-0 group-hover:bg-moana/20 transition-colors">
                        <Printer className="w-5 h-5 text-moana" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <p className="text-sm font-semibold text-fern-dark group-hover:text-moana transition-colors">{r.title}</p>
                          <span className="text-[9px] font-bold uppercase tracking-wider text-moana bg-moana/10 px-1.5 py-0.5 rounded">For Student</span>
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-2">{r.description}</p>
                        <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-moana">
                          <Download className="w-3.5 h-3.5" />
                          <span>Download blank template</span>
                        </div>
                      </div>
                    </button>

                    {/* Sibling: completed "answer sheet" for parents */}
                    {r.has_answer_sheet && (
                      <button
                        data-testid={`resource-${r.id}-answers`}
                        onClick={() => downloadFile(`${API}/resources/${reference}/${r.id}/pdf?answers=1${childParam.replace('?', '&')}`, `nestwise-${r.id}-answers.pdf`)}
                        className="flex items-start gap-3 bg-gradient-to-br from-kowhai-50 via-white to-kowhai-50/50 border-2 border-dashed border-kowhai/50 rounded-2xl p-4 text-left hover:border-kowhai hover:border-solid hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group relative overflow-hidden"
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-kowhai/5 rounded-full blur-2xl" />
                        <div className="w-10 h-10 rounded-xl bg-kowhai/20 flex items-center justify-center flex-shrink-0 group-hover:bg-kowhai/30 transition-colors relative">
                          <Sparkles className="w-5 h-5 text-kowhai-dark" />
                        </div>
                        <div className="flex-1 min-w-0 relative">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <p className="text-sm font-semibold text-fern-dark group-hover:text-kowhai-dark transition-colors">Worked example</p>
                            <span className="text-[9px] font-bold uppercase tracking-wider text-kowhai-dark bg-kowhai/20 px-1.5 py-0.5 rounded">For Parents</span>
                          </div>
                          <p className="text-xs text-slate-600 line-clamp-2">
                            A completed version to show <span className="font-medium text-fern-dark">how it&apos;s done</span> if your tamaiti gets stuck.
                          </p>
                          <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-kowhai-dark">
                            <Download className="w-3.5 h-3.5" />
                            <span>Download answer sheet</span>
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Phase pills */}
        <div className="flex flex-wrap gap-2 mb-8 no-print">
          {PHASES.map((p, i) => (
            <button key={p} data-testid={`phase-${p}`} onClick={() => setPhase(p)}
              className={`phase-pill ${phase === p ? `active ${PHASE_COLORS[p]}` : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'}`}>
              <span className="flex items-center gap-2">
                {i < phaseIdx && <Check className="w-3 h-3" />}
                {PHASE_LABELS[p]}
              </span>
            </button>
          ))}
        </div>

        {/* Phase content */}
        {renderPhase()}

        {/* Navigation */}
        <div className="flex justify-between mt-8 no-print">
          <Button data-testid="prev-phase" variant="outline" disabled={phaseIdx === 0}
            onClick={() => setPhase(PHASES[phaseIdx - 1])}
            className="rounded-full gap-2 border-slate-200 text-slate-600 hover:bg-fern/5 disabled:opacity-30">
            <ChevronLeft className="w-4 h-4" /> Previous
          </Button>
          <Button data-testid="save-progress-btn" variant="outline" onClick={() => saveProgress(false)}
            className="rounded-full gap-2 border-fern/30 text-fern hover:bg-fern/5">
            Save Progress
          </Button>
          <Button data-testid="next-phase" disabled={phaseIdx === PHASES.length - 1}
            onClick={() => setPhase(PHASES[phaseIdx + 1])}
            className="rounded-full gap-2 bg-fern hover:bg-fern-light text-white disabled:opacity-30">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </main>

      {/* Parent Mode confirmation — shown once per device on first activation */}
      <AlertDialog open={parentModeConfirmOpen} onOpenChange={setParentModeConfirmOpen}>
        <AlertDialogContent data-testid="parent-mode-confirm-dialog" className="rounded-3xl">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-2xl bg-kowhai/25 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-kowhai-dark" />
              </div>
              <AlertDialogTitle className="font-heading text-xl text-fern-dark">
                Switching to Parent Mode?
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-slate-600 text-sm leading-relaxed pt-1">
              Parent Mode reveals the full <strong>Answer Key</strong>, worked solutions, and
              teaching scripts right beside each question. Tamariki should stay in Student
              Mode while working through a lesson so they can solve the problems themselves.
              <br /><br />
              Turn this on when you are sitting alongside your child to mark their work —
              and flip back to Student Mode before they keep learning.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="parent-mode-cancel" className="rounded-full">
              Stay in Student Mode
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="parent-mode-confirm"
              onClick={confirmParentMode}
              className="rounded-full bg-fern hover:bg-fern-light"
            >
              Yes, show the answers
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
